import { VisuallyHidden } from '@ariakit/react';
import classNames from 'classnames';
import type React from 'react';
import Button from '../../../components/Button';
import Link from '../../../components/Link';
import PillBox from '../../../components/PillBox';
import Tooltip from '../../../components/Tooltip';
import { getWithCount } from '../../../services/Pluralize';
import styles from './style.m.less';

const specialStyleClassNames = {
    'coming-soon': styles.styleComingSoon,
};

export type SpecialNavItemStyles = keyof typeof specialStyleClassNames;

type BaseProps = {
    compact?: boolean;
    disabled?: boolean;
    icon: React.ReactNode;
    label: string;
    notification?: {
        count: number;
        type?: 'WARN' | 'INFO';
    };
    specialStyle?: SpecialNavItemStyles;
};

type LinkProps = BaseProps & React.ComponentProps<typeof Link> & { current?: boolean };

export function NavItemLink({
    icon,
    label,
    compact,
    className,
    disabled,
    current,
    notification,
    specialStyle,
    ...linkProps
}: LinkProps) {
    return (
        <CompactTooltipWrapper label={label} compact={compact}>
            <Link
                hasTextStyle={false}
                {...linkProps}
                className={classNames(
                    className,
                    compact ? styles.itemCompact : styles.item,
                    {
                        [styles.current]: current,
                        [styles.disabled]: disabled,
                    },
                    specialStyle && specialStyleClassNames[specialStyle]
                )}
                aria-disabled={disabled}
                aria-current={current}
            >
                <Content icon={icon} label={label} compact={compact} notification={notification} />
            </Link>
        </CompactTooltipWrapper>
    );
}

type ButtonProps = BaseProps & Omit<React.ComponentProps<typeof Button>, 'kind'>;

export function NavItemButton({
    icon,
    label,
    compact,
    className,
    notification,
    specialStyle,
    ...buttonProps
}: ButtonProps) {
    return (
        <CompactTooltipWrapper label={label} compact={compact}>
            <Button
                {...buttonProps}
                kind="PLAIN"
                className={classNames(className, styles.item, specialStyle && specialStyleClassNames[specialStyle])}
            >
                <Content icon={icon} label={label} compact={compact} notification={notification} />
            </Button>
        </CompactTooltipWrapper>
    );
}

function Content({
    icon,
    label,
    compact,
    notification,
}: Pick<BaseProps, 'icon' | 'label' | 'compact' | 'notification'>) {
    return (
        <>
            {icon} {compact ? <VisuallyHidden>{label}</VisuallyHidden> : <span className={styles.label}>{label}</span>}{' '}
            <NotificationIndicator notification={notification} compact={compact} />
        </>
    );
}

type TooltipWrapperProps = {
    children: React.ReactNode;
    label: string;
    compact?: boolean;
};

function CompactTooltipWrapper({ children, label, compact }: TooltipWrapperProps) {
    if (compact) {
        return (
            <Tooltip tooltip={label} position="right" instant>
                {children}
            </Tooltip>
        );
    }
    return <>{children}</>;
}

function NotificationIndicator({ notification, compact }: Pick<BaseProps, 'notification' | 'compact'>) {
    if (!notification?.count) {
        return null;
    }

    return (
        <span aria-label={getWithCount('Notification', notification.count)}>
            {compact ? (
                <span
                    className={
                        {
                            WARN: styles.notificationDotWarn,
                            INFO: styles.notificationDotInfo,
                        }[notification.type ?? 'WARN']
                    }
                />
            ) : (
                <PillBox
                    color={
                        (
                            {
                                WARN: 'RED',
                                INFO: 'BLUE',
                            } as const
                        )[notification.type ?? 'WARN']
                    }
                    value={notification.count}
                />
            )}
        </span>
    );
}
